import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const humanizeAge = (seconds) => {
  if (seconds == null) return null;
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  const days = Math.round(seconds / 86400);
  return `${days}d ago`;
};

const dotColor = (age, thresholds = [3600, 86400]) => {
  // Green if age < first threshold; amber if < second; red otherwise; grey if not connected.
  if (age == null) return "#52525B";
  if (age < thresholds[0]) return "#22C55E";
  if (age < thresholds[1]) return "#F59E0B";
  return "#EF4444";
};

const Pill = ({ label, status, ageLabel, testid }) => {
  let dot = "#52525B";
  let text = "Not connected";
  if (status?.connected) {
    if (status.age_seconds != null) {
      dot = dotColor(status.age_seconds);
      text = ageLabel;
    } else {
      dot = "#A1A1AA";
      text = status.property_id ? "cached: never" : "connected · awaiting sync";
    }
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded bg-[#0F0F0F] border border-[#2A2A2A]" data-testid={testid}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
      <span className="text-[#A1A1AA]">{label}</span>
      <span className="text-white font-mono">{text}</span>
    </span>
  );
};

const FreshnessBar = ({ clientId, month }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/intelligence/${clientId}/freshness`, { params: { month } });
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [clientId, month]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const sh = data.shopify || {};
  const meta = data.meta || {};
  const ga = data.ga4 || {};

  // Pick the freshest Meta signal (either ad-spend sync or campaigns sync)
  let metaForPill = { ...meta };
  if (meta.connected) {
    const metaAge = meta.age_seconds;
    metaForPill.age_seconds = metaAge;
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="freshness-bar">
      <Pill
        label="Shopify ·"
        status={sh}
        ageLabel={sh.age_seconds != null ? `synced ${humanizeAge(sh.age_seconds)}` : "no snapshot for this month"}
        testid="freshness-shopify"
      />
      <Pill
        label="Meta ·"
        status={metaForPill}
        ageLabel={meta.age_seconds != null ? `synced ${humanizeAge(meta.age_seconds)}` : "no spend synced this month"}
        testid="freshness-meta"
      />
      <Pill
        label="GA4 ·"
        status={ga}
        ageLabel={ga.age_seconds != null ? `cached ${humanizeAge(ga.age_seconds)}` : "no property selected"}
        testid="freshness-ga4"
      />
      <button
        onClick={load}
        className="text-[10px] px-2 py-0.5 rounded text-[#71717A] hover:text-white"
        title="Refresh freshness check"
        data-testid="freshness-refresh"
      >
        ⟳
      </button>
    </div>
  );
};

export default FreshnessBar;
